import { createToaster, Toast, ToastProvider } from "@etudescarre/ui";
import { withSentryRouting } from "@sentry/react";
import { Navigate, Route, Routes } from "react-router";
import { AdminLayout } from "./components/admin-layout";
import { ConfirmDialogHost } from "./components/confirm-dialog";
import { ProtectedLayout } from "./components/protected-layout";
import { RequireStaff } from "./components/require-staff";
import {
  AdminInfoPage,
  CompaniesList,
  CompanyStatusesPage,
  ContractDetailPage,
  ContractsList,
  EmployeesList,
  HistoryList,
  TeamDetailPage,
  TeamsList,
  UsersList,
  YardDecoupagePage,
  YardDonneursOrdrePage,
  YardNatureTravauxPage,
  YardSettingsMenu,
  YardsList,
  YardTravauxPage,
} from "./features/admin";
import ChangePassword from "./features/auth/change-password";
import ForgotPassword from "./features/auth/forgot-password";
import Login from "./features/auth/login";
import ResetPassword from "./features/auth/reset-password";
import SignOut from "./features/auth/sign-out";
import Home from "./features/home";
import { NotFound } from "./features/not-found";
import { OrganizationSelect } from "./features/organizations/organization-select";
import { StaffAnnuaire } from "./features/staff/staff-annuaire";
import { StaffHome } from "./features/staff/staff-home";
import { YardHome, YardLayout, YardSettings } from "./features/yard";
import { AvancementEtudes } from "./features/yard/pages/avancement-etudes";
import {
  ClasseurBordereauxEmisDO,
  ClasseurBordereauxEmisPresta,
  ClasseurBordereauxRecus,
  ClasseurBordereauxRemis,
} from "./features/yard/pages/classeur-bordereaux";
import { ClasseurRapports } from "./features/yard/pages/classeur-rapports";
import { ClasseurReceptions } from "./features/yard/pages/classeur-receptions";
import { ClasseurReponsesRapports } from "./features/yard/pages/classeur-reponses-rapports";
import { ClasseurVisas } from "./features/yard/pages/classeur-visas";
import { ClasseurVisasRecus } from "./features/yard/pages/classeur-visas-recus";
import { ContratsEquipes } from "./features/yard/pages/contrats-equipes";
import { CRReunion } from "./features/yard/pages/cr-reunion";
import { DiffusionDO } from "./features/yard/pages/diffusion-do";
import { DiffusionPresta } from "./features/yard/pages/diffusion-presta";
import { DocumentsList } from "./features/yard/pages/documents-list";
import { DossierMarche } from "./features/yard/pages/dossier-marche";
import { EnregistrerReception } from "./features/yard/pages/enregistrer-reception";
import { FichesQR } from "./features/yard/pages/fiches-qr-classeur";
import { FichesQREchanges } from "./features/yard/pages/fiches-qr-echanges";
import { FTMClasseur } from "./features/yard/pages/ftm-classeur";
import { GenererReponseRapport } from "./features/yard/pages/generer-reponse-rapport";
import { InterventionsAVenir } from "./features/yard/pages/interventions-a-venir";
import { MisesAuCourrier } from "./features/yard/pages/mises-au-courrier";
import { MisesAuCourrierEffective } from "./features/yard/pages/mises-au-courrier-effective";
import {
  ModelesBIM,
  ModelesBIMConception,
} from "./features/yard/pages/modeles-bim";
import { ObservationsSansReponse } from "./features/yard/pages/observations-sans-reponse";
import { OSClasseur } from "./features/yard/pages/os-classeur";
import { PreparerReponses } from "./features/yard/pages/preparer-reponses";
import { Propositions } from "./features/yard/pages/propositions";
import { ReceptionRapportsSas } from "./features/yard/pages/reception-rapports-sas";
import { ReceptionSas } from "./features/yard/pages/reception-sas";
import { RemiseCourrierMainPropre } from "./features/yard/pages/remise-courrier-main-propre";
import { RemiseMainPropre } from "./features/yard/pages/remise-main-propre";
import { RemisesEnMainPropre } from "./features/yard/pages/remises-en-main-propre";
import { StatsVisas } from "./features/yard/pages/stats-visas";
import { TraiterObservations } from "./features/yard/pages/traiter-observations";
import { TravauxZoning } from "./features/yard/pages/travaux-zoning";
import { SessionProvider } from "./providers/session-provider";

const SentryRoutes = withSentryRouting(Routes);

export const toaster = createToaster({
  placement: "top-end",
  gap: 16,
  duration: 5000,
});

function App() {
  return (
    <SessionProvider>
      <SentryRoutes>
        <Route element={<Home />} path="/" />
        <Route element={<Login />} path="/auth/login" />
        <Route element={<SignOut />} path="/auth/sign-out" />
        <Route element={<ForgotPassword />} path="/auth/forgot-password" />
        <Route element={<ResetPassword />} path="/auth/reset-password" />
        <Route element={<ProtectedLayout />}>
          <Route element={<Home />} path="/home" />
          <Route
            element={
              <RequireStaff>
                <StaffHome />
              </RequireStaff>
            }
            path="/staff"
          />
          <Route
            element={
              <RequireStaff>
                <StaffAnnuaire />
              </RequireStaff>
            }
            path="/staff/annuaire"
          />
          <Route
            element={<Navigate to="/organizations/select" replace />}
            path="/yards/select"
          />
          <Route
            element={<OrganizationSelect />}
            path="/organizations/select"
          />
          <Route element={<YardLayout />} path="/yards/:yardId">
            <Route index element={<YardHome />} />
            <Route path="documents" element={<DocumentsList />} />
            <Route path="diffusion-do" element={<DiffusionDO />} />
            <Route path="reception-sas" element={<ReceptionSas />} />
            <Route
              path="enregistrer-reception"
              element={<EnregistrerReception />}
            />
            <Route path="remise-main-propre" element={<RemiseMainPropre />} />
            <Route
              path="remise-courrier-main-propre"
              element={<RemiseCourrierMainPropre />}
            />
            <Route path="diffusion-presta" element={<DiffusionPresta />} />
            <Route path="mises-au-courrier" element={<MisesAuCourrier />} />
            <Route
              path="mises-au-courrier-effective"
              element={<MisesAuCourrierEffective />}
            />
            <Route
              path="remises-en-main-propre"
              element={<RemisesEnMainPropre />}
            />
            <Route path="rapports" element={<ClasseurRapports />} />
            <Route
              path="reception-rapports"
              element={<ReceptionRapportsSas />}
            />
            <Route
              path="traiter-observations"
              element={<TraiterObservations />}
            />
            <Route path="preparer-reponses" element={<PreparerReponses />} />
            <Route
              path="observations-sans-reponse"
              element={<ObservationsSansReponse />}
            />
            <Route
              path="generer-reponse-rapport"
              element={<GenererReponseRapport />}
            />
            <Route
              path="bordereaux-emis-do"
              element={<ClasseurBordereauxEmisDO />}
            />
            <Route
              path="bordereaux-emis-presta"
              element={<ClasseurBordereauxEmisPresta />}
            />
            <Route
              path="bordereaux-recus"
              element={<ClasseurBordereauxRecus />}
            />
            <Route
              path="bordereaux-remis"
              element={<ClasseurBordereauxRemis />}
            />
            <Route
              path="reponses-rapports"
              element={<ClasseurReponsesRapports />}
            />
            <Route path="propositions" element={<Propositions />} />
            <Route path="visas" element={<ClasseurVisas />} />
            <Route path="visas-recus" element={<ClasseurVisasRecus />} />
            <Route path="receptions" element={<ClasseurReceptions />} />
            <Route path="stats-visas" element={<StatsVisas />} />
            <Route path="avancement" element={<AvancementEtudes />} />
            <Route path="interventions" element={<InterventionsAVenir />} />
            <Route path="bim-execution" element={<ModelesBIM />} />
            <Route path="bim-conception" element={<ModelesBIMConception />} />
            <Route path="dossier-marche" element={<DossierMarche />} />
            <Route path="ftm" element={<FTMClasseur />} />
            <Route path="os" element={<OSClasseur />} />
            <Route path="fiches-qr" element={<FichesQR />} />
            <Route path="echanges/fiches-qr" element={<FichesQREchanges />} />
            <Route path="cr-reunion" element={<CRReunion />} />
            <Route path="settings" element={<YardSettings />} />
            <Route path="contrats-equipes" element={<ContratsEquipes />} />
            <Route path="travaux-zoning" element={<TravauxZoning />} />
          </Route>
          <Route element={<ChangePassword />} path="/auth/change-password" />
          <Route element={<AdminLayout />} path="/admin">
            <Route element={<YardsList />} index />
            <Route element={<YardsList />} path="yards" />
            <Route element={<YardSettingsMenu />} path="yards/:yardId" />
            <Route
              element={<YardDecoupagePage />}
              path="yards/:yardId/decoupage"
            />
            <Route
              element={<YardNatureTravauxPage />}
              path="yards/:yardId/nature-travaux"
            />
            <Route element={<YardTravauxPage />} path="yards/:yardId/travaux" />
            <Route
              element={<YardDonneursOrdrePage />}
              path="yards/:yardId/donneurs-ordre"
            />
            <Route element={<CompaniesList />} path="companies" />
            <Route
              element={<CompanyStatusesPage />}
              path="companies/:companyId/statuses"
            />
            <Route element={<ContractsList />} path="contracts" />
            <Route
              element={<ContractDetailPage />}
              path="contracts/:contractId"
            />
            <Route element={<EmployeesList />} path="employees" />
            <Route element={<TeamsList />} path="teams" />
            <Route element={<TeamDetailPage />} path="teams/:teamId" />
            <Route element={<UsersList />} path="users" />
            <Route element={<HistoryList />} path="history" />
            <Route element={<AdminInfoPage />} path="info" />
          </Route>
          <Route element={<NotFound />} path="*" />
        </Route>
      </SentryRoutes>
      <ConfirmDialogHost />
      <ToastProvider
        toaster={toaster}
        className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm"
      >
        {(toast) => (
          <Toast
            key={toast.id}
            type={toast.type as "info" | "success" | "warning" | "error"}
          >
            <Toast.Title>{toast.title}</Toast.Title>
            <Toast.Description>{toast.description}</Toast.Description>
          </Toast>
        )}
      </ToastProvider>
    </SessionProvider>
  );
}

export default App;
